import { I18n } from '@/i18n/I18n';
import { cn } from '@/frontend/lib/utils';
import { FileRef, InputSheet, InputSheetSection, InputValues, SectionProgress, SheetInput, SweepCellChange, TableInputRow, traceKey } from '@/reports/inputs';
import { ChipsControl, DateControl, Field, NumberControl, Segmented, TextControl } from './fields';
import { ImageControl, FileControl, WaveformControl } from './fileFields';
import { inputAnchor, useFill } from './fillContext';
import { SweepControl } from './SweepControl';
import { TableControl } from './TableControl';

/** Kinds that take the whole width of a section; the rest sit two to a row. */
export const WIDE = new Set(['names', 'numbers', 'image', 'file', 'waveform', 'table', 'sweep']);

/** What an input asks for, at the right of its label: "Names · 3", "Images · one or more". */
export function hintFor(input: SheetInput, value: unknown): string | undefined {
  const count = Array.isArray(value) ? value.length : 0;
  switch (input.kind) {
    case 'names': return I18n.get('rv2HintNames', { count: String(count) });
    case 'numbers': return I18n.get('rv2HintNumbers', { unit: input.unit ? ` · ${input.unit}` : '', count: String(count) });
    case 'image': return input.multiple ? I18n.get('rv2HintImages') : I18n.get('rv2HintImage');
    case 'file': return input.accept?.length ? I18n.get('rv2HintFileTypes', { types: input.accept.join(', ') }) : I18n.get('rv2HintFile');
    case 'waveform': return input.help ? undefined : I18n.get('rv2HintWaveform');
    case 'table': return I18n.get('rv2HintTable');
    default: return undefined;
  }
}

/** An input set to a value; for a sweep, the cell input that was set. */
export type InputChange = (id: string, value: unknown, cell?: SweepCellChange) => void;

/** One input, drawn for its kind. */
export function InputControl({ sheet, input, inputs, onChange }: { sheet: InputSheet; input: SheetInput; inputs: InputValues; onChange: InputChange }) {
  const { editable, traces } = useFill();
  const value = inputs[input.id];
  const set = (v: unknown) => onChange(input.id, v);
  const id = `field-${input.id}`;
  switch (input.kind) {
    case 'number':
      return <NumberControl id={id} value={(value as number | null) ?? null} unit={input.unit} disabled={!editable} onChange={set} />;
    case 'text':
      return <TextControl id={id} value={(value as string) ?? ''} multiline={input.multiline} disabled={!editable} onChange={set} />;
    case 'choice':
      return <Segmented id={id} disabled={!editable} value={(value as string) || null} onChange={(v) => set(v ?? '')} options={input.choices.map((c) => ({ value: c, label: c }))} />;
    case 'yesno':
      return <Segmented id={id} disabled={!editable} value={typeof value === 'boolean' ? value : null} onChange={set} options={[{ value: true, label: I18n.get('yes') }, { value: false, label: I18n.get('no') }]} />;
    case 'date':
      return <DateControl id={id} value={(value as string) ?? ''} disabled={!editable} onChange={set} />;
    case 'names':
    case 'numbers':
      return <ChipsControl id={id} input={input} disabled={!editable} value={(value as Array<string | number>) ?? []} onChange={set} />;
    case 'image':
      return <ImageControl id={id} value={(value as FileRef[]) ?? []} multiple={input.multiple} onChange={set} />;
    case 'file':
      return <FileControl id={id} value={(value as FileRef[]) ?? []} multiple={input.multiple} accept={input.accept} onChange={set} />;
    case 'waveform':
      return <WaveformControl id={id} input={input} value={(value as FileRef | null) ?? null} trace={traces[traceKey(input.id)]} onChange={set} />;
    case 'table':
      return <TableControl id={id} input={input} value={(value as TableInputRow[]) ?? []} onChange={set} />;
    case 'sweep':
      return <SweepControl id={id} sheet={sheet} sweep={input} inputs={inputs} onChange={(v, cell) => onChange(input.id, v, cell)} />;
  }
}

/** What a section's header says it holds: its inputs, or for one with a sweep, its readings and how many are empty. */
function sectionSummary(section: InputSheetSection, progress: SectionProgress | undefined, runnerInputs: Set<string>): string {
  const hasSweep = section.inputs.some((i) => i.kind === 'sweep');
  const byRunner = section.inputs.some((i) => runnerInputs.has(i.id));
  if (hasSweep && progress) {
    const empty = progress.total - progress.filled;
    return empty ? I18n.get('rv2SectionReadingsEmpty', { count: String(progress.total), empty: String(empty) }) : I18n.get('rv2SectionReadings', { count: String(progress.total) });
  }
  const base = I18n.get('rv2SectionInputs', { count: String(section.inputs.length) });
  return byRunner ? `${base} · ${I18n.get('rv2SetByRunner')}` : base;
}

/** The sheet as cards, one per section, each input drawn for its kind. */
export function SheetForm({ sheet, inputs, progress, onChange }: { sheet: InputSheet; inputs: InputValues; progress: SectionProgress[]; onChange: InputChange }) {
  const runnerInputs = new Set(sheet.sections.flatMap((s) => s.inputs.flatMap((i) => (i.kind === 'sweep' ? i.conditions.flatMap((c) => (c.input ? [c.input] : [])) : []))));
  return (
    <div className="flex min-w-0 flex-col gap-[1rem]">
      {sheet.sections.map((section) => {
        const sectionProgress = progress.find((p) => p.id === section.id);
        return (
          <section key={section.id} id={`section-${section.id}`} aria-labelledby={`section-title-${section.id}`} className="scroll-mt-24 rounded-xl border-[1px] bg-background px-[1.25rem] py-[1rem] shadow-xs">
            <header className="mb-[0.75rem] flex items-baseline justify-between gap-[0.75rem]">
              <h2 id={`section-title-${section.id}`} className="text-base font-semibold">{section.title}</h2>
              <span className="text-xs text-muted-foreground">{sectionSummary(section, sectionProgress, runnerInputs)}</span>
            </header>
            {section.help && <p className="-mt-1.5 mb-[0.75rem] max-w-3xl text-sm text-muted-foreground">{section.help}</p>}
            <div className="grid grid-cols-1 gap-x-[1.25rem] gap-y-[1rem] md:grid-cols-2">
              {section.inputs.map((input) => {
                const wide = WIDE.has(input.kind) || (input.kind === 'text' && !!input.multiline);
                const boxed = runnerInputs.has(input.id);
                return (
                  <div key={input.id} className={cn('min-w-0', wide && 'md:col-span-2', boxed && 'rounded-lg border-[1px] px-[1rem] py-[0.75rem]')}>
                    <Field anchor={inputAnchor(input.id)} input={input} hint={hintFor(input, inputs[input.id])} wide={wide}>
                      <InputControl sheet={sheet} input={input} inputs={inputs} onChange={onChange} />
                    </Field>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
